const FLOWPLAYER_VIDEO_SELECTOR = '[data-flowplayer-video]';
const HLS_MIME_TYPE = 'application/vnd.apple.mpegurl';
const HLS_STREAM_INFO_TAG = '#EXT-X-STREAM-INF:';
const HOMEPAGE_HERO_MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 749px)');

/** @type {WeakSet<HTMLVideoElement>} */
const initializedVideos = new WeakSet();

/** @type {WeakMap<HTMLVideoElement, object>} */
const initializationRuns = new WeakMap();

/** @type {WeakMap<HTMLVideoElement, HlsInstance>} */
const hlsPlayers = new WeakMap();

/** @type {WeakMap<HTMLVideoElement, EventListener>} */
const nativePlaybackErrorHandlers = new WeakMap();

/** @type {WeakMap<HTMLVideoElement, AbortController>} */
const playlistRequests = new WeakMap();

/** @type {Promise<HlsConstructor> | null} */
let hlsLibraryPromise = null;

/**
 * Read the video renditions advertised by a Flowplayer multivariant playlist.
 * Variant file names and available resolutions differ between videos, so the
 * master playlist is the only reliable source of truth.
 *
 * @param {string} masterUrl
 * @param {string} playlist
 * @returns {FlowplayerVariant[]}
 */
function parseFlowplayerVariants(masterUrl, playlist) {
  const lines = playlist
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const variants = [];

  lines.forEach((line, index) => {
    if (!line.startsWith(HLS_STREAM_INFO_TAG)) {
      return;
    }

    let variantPath;

    for (let candidateIndex = index + 1; candidateIndex < lines.length; candidateIndex += 1) {
      const candidate = lines[candidateIndex];

      if (candidate.startsWith(HLS_STREAM_INFO_TAG)) {
        break;
      }

      if (!candidate.startsWith('#')) {
        variantPath = candidate;
        break;
      }
    }

    if (!variantPath) {
      return;
    }

    const resolution = line.match(/RESOLUTION=(\d+)x(\d+)/i);
    const averageBandwidth = line.match(/AVERAGE-BANDWIDTH=(\d+)/i);
    const bandwidth = line.match(/(?:^|[:,])BANDWIDTH=(\d+)/i);

    try {
      const url = new URL(variantPath, masterUrl);
      const master = new URL(masterUrl);

      // Preserve a token from a signed master URL when relative rendition URLs
      // do not include their own query string.
      if (!url.search && master.search) {
        url.search = master.search;
      }

      variants.push({
        url: url.href,
        width: resolution ? Number(resolution[1]) : 0,
        height: resolution ? Number(resolution[2]) : 0,
        bandwidth: Number(averageBandwidth?.[1] || bandwidth?.[1] || 0),
      });
    } catch (error) {
      // Ignore malformed rendition URLs and keep parsing the other entries.
    }
  });

  return variants;
}

/**
 * Select the least expensive rendition that can cover the rendered video at
 * the device's pixel density. Use the largest available rendition when none is
 * large enough, and bandwidth as a fallback when resolution metadata is absent.
 *
 * @param {HTMLVideoElement} video
 * @param {FlowplayerVariant[]} variants
 * @returns {FlowplayerVariant | null}
 */
function selectFlowplayerVariant(video, variants) {
  const sortedVariants = variants.slice().sort((first, second) => {
    const firstPixels = first.width * first.height;
    const secondPixels = second.width * second.height;

    return firstPixels - secondPixels || first.bandwidth - second.bandwidth;
  });

  if (!sortedVariants.length) {
    return null;
  }

  const videoRect = video.getBoundingClientRect();
  const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
  const targetWidth = Math.ceil(Math.max(videoRect.width, video.clientWidth) * pixelRatio);
  const targetHeight = Math.ceil(Math.max(videoRect.height, video.clientHeight) * pixelRatio);
  const renditionWithResolution = sortedVariants.filter((variant) => variant.width && variant.height);

  if (!targetWidth || !targetHeight || !renditionWithResolution.length) {
    return sortedVariants[sortedVariants.length - 1];
  }

  return (
    renditionWithResolution.find((variant) => variant.width >= targetWidth && variant.height >= targetHeight) ||
    renditionWithResolution[renditionWithResolution.length - 1]
  );
}

/**
 * Resolve a stable rendition before playback. Very short background videos can
 * finish and loop before native HLS has enough time to leave its lowest initial
 * quality, so relying on adaptive playback alone can keep them visibly blurry.
 *
 * @param {HTMLVideoElement} video
 * @param {string} masterUrl
 * @returns {Promise<string>}
 */
async function selectFlowplayerPlaybackUrl(video, masterUrl) {
  const controller = new AbortController();
  playlistRequests.set(video, controller);

  try {
    const response = await fetch(masterUrl, { signal: controller.signal });

    if (!response.ok) {
      return masterUrl;
    }

    const playlist = await response.text();
    const selectedVariant = selectFlowplayerVariant(video, parseFlowplayerVariants(masterUrl, playlist));

    if (!selectedVariant) {
      return masterUrl;
    }

    video.dataset.flowplayerSelectedUrl = selectedVariant.url;

    if (selectedVariant.width && selectedVariant.height) {
      video.dataset.flowplayerSelectedResolution = `${selectedVariant.width}x${selectedVariant.height}`;
    }

    return selectedVariant.url;
  } catch (error) {
    return masterUrl;
  } finally {
    if (playlistRequests.get(video) === controller) {
      playlistRequests.delete(video);
    }
  }
}

/**
 * Load the vendored HLS playback engine only when native HLS is unavailable.
 *
 * @param {string} libraryUrl
 * @returns {Promise<HlsConstructor>}
 */
function loadHlsLibrary(libraryUrl) {
  if (window.Hls) {
    return Promise.resolve(window.Hls);
  }

  if (hlsLibraryPromise) {
    return hlsLibraryPromise;
  }

  hlsLibraryPromise = new Promise((resolve, reject) => {
    const handleLoad = () => {
      if (window.Hls) {
        resolve(window.Hls);
      } else {
        document.querySelector('script[data-flowplayer-hls-library]')?.remove();
        reject(new Error('The HLS playback engine did not initialize.'));
      }
    };
    const handleError = () => {
      document.querySelector('script[data-flowplayer-hls-library]')?.remove();
      reject(new Error('The HLS playback engine could not be loaded.'));
    };
    const existingScript = document.querySelector('script[data-flowplayer-hls-library]');

    if (existingScript instanceof HTMLScriptElement) {
      // A live request is already represented by hlsLibraryPromise. If there is
      // a script without a matching promise or Hls global, it is a stale failed
      // attempt whose load/error events will not fire again.
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = libraryUrl;
    script.async = true;
    script.dataset.flowplayerHlsLibrary = '';
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  hlsLibraryPromise.catch(() => {
    hlsLibraryPromise = null;
  });

  return hlsLibraryPromise;
}

/**
 * A configured mobile image replaces Flowplayer in the Homepage Hero at the
 * same breakpoint used by the component's responsive CSS.
 *
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function isHomepageHeroMobileMediaActive(video) {
  return (
    HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.matches &&
    video.closest('.comp-homepage-hero--has-mobile-media') !== null
  );
}

/**
 * @param {HTMLVideoElement} video
 * @param {object} initializationRun
 * @returns {boolean}
 */
function isCurrentInitialization(video, initializationRun) {
  return (
    video.isConnected &&
    initializedVideos.has(video) &&
    initializationRuns.get(video) === initializationRun &&
    !isHomepageHeroMobileMediaActive(video)
  );
}

/**
 * Stop playback and release any network-backed media resources.
 *
 * @param {HTMLVideoElement} video
 * @param {boolean} [resetInitialization]
 */
function clearFlowplayerPlayback(video, resetInitialization = false) {
  const hls = hlsPlayers.get(video);
  const nativeErrorHandler = nativePlaybackErrorHandlers.get(video);
  const playlistRequest = playlistRequests.get(video);

  if (playlistRequest) {
    playlistRequest.abort();
    playlistRequests.delete(video);
  }

  if (hls) {
    hls.destroy();
    hlsPlayers.delete(video);
  }

  if (nativeErrorHandler) {
    video.removeEventListener('error', nativeErrorHandler);
    nativePlaybackErrorHandlers.delete(video);
  }

  video.pause();

  if (video.hasAttribute('src') || video.currentSrc) {
    video.removeAttribute('src');
    video.load();
  }

  delete video.dataset.flowplayerSelectedUrl;
  delete video.dataset.flowplayerSelectedResolution;
  delete video.dataset.flowplayerMasterRetried;

  if (resetInitialization) {
    initializedVideos.delete(video);
    initializationRuns.delete(video);
  }
}

/**
 * Reveal the component's image fallback when playback cannot be recovered.
 *
 * @param {HTMLVideoElement} video
 */
function showFlowplayerFallback(video) {
  const container = video.closest('.comp-homepage-hero, .is-flowplayer-video, .is-flowplayer-video-bg');
  clearFlowplayerPlayback(video);
  video.removeAttribute('autoplay');

  const hasFallback =
    container?.classList.contains('comp-homepage-hero') ||
    container?.querySelector('.comp-textpic__video-fallback, .comp-textpic__video-fallback-picture');

  if (hasFallback) {
    container?.classList.add('has-flowplayer-error');
  }
}

/**
 * Request muted autoplay without leaving an unhandled play promise in browsers
 * where the visitor has explicitly disabled all autoplay.
 *
 * @param {HTMLVideoElement} video
 * @param {object} initializationRun
 */
async function autoplay(video, initializationRun) {
  try {
    await video.play();
  } catch (error) {
    if (
      !isCurrentInitialization(video, initializationRun) ||
      (error instanceof DOMException && error.name === 'AbortError')
    ) {
      return;
    }

    showFlowplayerFallback(video);
  }
}

/**
 * Initialize native HLS playback in Safari and other browsers that support it.
 *
 * @param {HTMLVideoElement} video
 * @param {string} sourceUrl
 * @param {() => void} handleError
 * @param {object} initializationRun
 */
function initNativePlayback(video, sourceUrl, handleError, initializationRun) {
  const handleNativeError = () => {
    nativePlaybackErrorHandlers.delete(video);

    if (isCurrentInitialization(video, initializationRun)) {
      handleError();
    }
  };

  nativePlaybackErrorHandlers.set(video, handleNativeError);
  video.addEventListener('error', handleNativeError, { once: true });
  video.src = sourceUrl;
  video.load();
  autoplay(video, initializationRun);
}

/**
 * Initialize Media Source Extensions playback for Firefox and other browsers
 * without native HLS support.
 *
 * @param {HTMLVideoElement} video
 * @param {string} sourceUrl
 * @param {HlsConstructor} Hls
 * @param {() => void} handleFatalError
 * @param {object} initializationRun
 */
function initHlsPlayback(video, sourceUrl, Hls, handleFatalError, initializationRun) {
  const hls = new Hls();
  let networkRecoveryAttempted = false;
  let mediaRecoveryAttempted = false;
  let fatalErrorHandled = false;

  hlsPlayers.set(video, hls);

  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    if (isCurrentInitialization(video, initializationRun)) {
      autoplay(video, initializationRun);
    }
  });
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (!isCurrentInitialization(video, initializationRun) || !data.fatal || fatalErrorHandled) {
      return;
    }

    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !networkRecoveryAttempted) {
      networkRecoveryAttempted = true;
      hls.startLoad();
      return;
    }

    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecoveryAttempted) {
      mediaRecoveryAttempted = true;
      hls.recoverMediaError();
      return;
    }

    fatalErrorHandled = true;
    handleFatalError();
  });

  hls.loadSource(sourceUrl);
  hls.attachMedia(video);
}

/**
 * @param {HTMLVideoElement} video
 */
async function initFlowplayerVideo(video) {
  if (
    initializedVideos.has(video) ||
    isHomepageHeroMobileMediaActive(video) ||
    video.closest('.has-flowplayer-error')
  ) {
    return;
  }

  initializedVideos.add(video);
  const initializationRun = {};
  initializationRuns.set(video, initializationRun);

  const masterUrl = video.dataset.flowplayerMasterUrl;
  const hlsLibraryUrl = video.dataset.hlsLibraryUrl;

  if (!masterUrl) {
    showFlowplayerFallback(video);
    return;
  }

  // Set both the default and current muted states before requesting autoplay.
  video.defaultMuted = true;
  video.muted = true;

  const supportsNativeHls = Boolean(video.canPlayType(HLS_MIME_TYPE));
  const playbackUrlPromise = selectFlowplayerPlaybackUrl(video, masterUrl);

  if (supportsNativeHls) {
    const playbackUrl = await playbackUrlPromise;

    if (!isCurrentInitialization(video, initializationRun)) {
      return;
    }

    let masterRetried = playbackUrl === masterUrl;
    const handleError = () => {
      if (!masterRetried) {
        masterRetried = true;
        video.dataset.flowplayerMasterRetried = 'true';
        initNativePlayback(video, masterUrl, () => showFlowplayerFallback(video), initializationRun);
        return;
      }

      showFlowplayerFallback(video);
    };

    initNativePlayback(video, playbackUrl, handleError, initializationRun);
    return;
  }

  if (!hlsLibraryUrl) {
    showFlowplayerFallback(video);
    return;
  }

  try {
    const [Hls, playbackUrl] = await Promise.all([loadHlsLibrary(hlsLibraryUrl), playbackUrlPromise]);

    if (!isCurrentInitialization(video, initializationRun)) {
      return;
    }

    if (!Hls.isSupported()) {
      showFlowplayerFallback(video);
      return;
    }

    let masterRetried = playbackUrl === masterUrl;
    const handleFatalError = () => {
      if (!masterRetried) {
        masterRetried = true;
        video.dataset.flowplayerMasterRetried = 'true';
        hlsPlayers.get(video)?.destroy();
        hlsPlayers.delete(video);
        initHlsPlayback(
          video,
          masterUrl,
          Hls,
          () => showFlowplayerFallback(video),
          initializationRun
        );
        return;
      }

      showFlowplayerFallback(video);
    };

    initHlsPlayback(video, playbackUrl, Hls, handleFatalError, initializationRun);
  } catch (error) {
    if (isCurrentInitialization(video, initializationRun)) {
      showFlowplayerFallback(video);
    }
  }
}

/**
 * @param {ParentNode} root
 */
function syncFlowplayerVideos(root = document) {
  root.querySelectorAll(FLOWPLAYER_VIDEO_SELECTOR).forEach((element) => {
    if (element instanceof HTMLVideoElement) {
      if (isHomepageHeroMobileMediaActive(element)) {
        if (initializedVideos.has(element)) {
          clearFlowplayerPlayback(element, true);
        }
      } else {
        initFlowplayerVideo(element);
      }
    }
  });
}

syncFlowplayerVideos();

const handleHomepageHeroBreakpointChange = () => syncFlowplayerVideos();

if (typeof HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.addEventListener === 'function') {
  HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.addEventListener('change', handleHomepageHeroBreakpointChange);
} else {
  HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.addListener(handleHomepageHeroBreakpointChange);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    syncFlowplayerVideos(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) {
    return;
  }

  event.target.querySelectorAll(FLOWPLAYER_VIDEO_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLVideoElement)) {
      return;
    }

    clearFlowplayerPlayback(element, true);
  });
});

/**
 * @typedef {object} FlowplayerVariant
 * @property {string} url
 * @property {number} width
 * @property {number} height
 * @property {number} bandwidth
 */
